/**
 * L'onglet Rapports de la fiche d'un élève : `(état, élève) → la liste` et `(état, réponse) → le
 * diagnostic` (patron A pour la liste, B pour le diagnostic).
 *
 * La liste est celle du parent (`liveReportsViewModel` : la même tranche `liveReports.liveReports`,
 * la même attente, la même erreur de pied), réduite à CET élève (`reportsOfStudent`) : au large, la
 * tranche porte encore l'élève d'avant tant que la page 1 du suivant n'est pas arrivée — la page
 * montre alors sa forme, pas les rapports d'un autre.
 *
 * Ce sont des fabriques : la fiche peut être montée deux fois (à droite de Mes élèves, et poussée),
 * chacune avec son élève — un `createSelector` partagé ne garde qu'une entrée (`ports/CLAUDE.md`).
 */
import { createSelector } from "reselect"

import {
    reportsOfStudent,
    toUITutorStudentDiagnostics,
    type ApiReportWithDiagnostic,
    type ApiTutorStudentReportRow,
    type StoredDiagnostic,
} from "../../../adapters/tutor/Students/tutorStudentReportsAdapter"
import { type LiveReportsViewModel, toLiveReportsViewModel } from "../../parent/LiveReports/liveReportsViewModel"
import { type UITutorStudentDiagnostic } from "../../../ui/types/tutorEleves"

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any
type StudentId = string | number | null | undefined

export function makeTutorStudentReportsViewModel() {
    return createSelector(
        [
            (state: ReduxState) => state?.data?.liveReports?.liveReports as ApiTutorStudentReportRow[] | undefined,
            (state: ReduxState) => state?.data?.staticData?.school_subjects,
            (state: ReduxState) => state?.api?.getStudentLiveReports,
            (_state: ReduxState, studentId: StudentId) => studentId,
        ],
        (rows, subjects, req, studentId): LiveReportsViewModel =>
            toLiveReportsViewModel(reportsOfStudent(rows, studentId), subjects, req)
    )
}

/** Le diagnostic : les rapports qui en portent un (la réponse), et le texte que le réducteur a gardé. */
export function makeTutorStudentDiagnosticViewModel() {
    return createSelector(
        [
            (state: ReduxState) => state?.data?.liveReports?.diagnostics as StoredDiagnostic[] | undefined,
            (_state: ReduxState, raw: ApiReportWithDiagnostic[] | null) => raw,
        ],
        (stored, raw): UITutorStudentDiagnostic[] => toUITutorStudentDiagnostics(raw, stored)
    )
}
